import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Select,
  Button,
  Table,
  Typography,
  Row,
  Col,
  Spin,
  message,
  Progress,
  Space,
  Tooltip,
  Input,
  Tag,
  Empty
} from 'antd';
import {
  CheckOutlined,
  ReloadOutlined,
  DownloadOutlined,
  CalendarOutlined,
  FilterOutlined,
  SearchOutlined,
  ClearOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import { loamsApi } from '@/services/api';
import './WorkProgress.css';

const { Title, Text } = Typography;
const { Option } = Select;

// Color constants
const PHOENICIAN_GOLD = '#A2732E';
const MONTH_ACTIVE_BG = '#FFF8E6';
const MONTH_UPLOADED_BG = '#E6F7E6';
const CERTIFICATE_CHECK_COLOR = '#52c41a';

// Month names for columns
const MONTHS = [
  { key: 'jan', label: 'Jan', num: '01' },
  { key: 'feb', label: 'Feb', num: '02' },
  { key: 'mar', label: 'Mar', num: '03' },
  { key: 'apr', label: 'Apr', num: '04' },
  { key: 'may', label: 'May', num: '05' },
  { key: 'jun', label: 'Jun', num: '06' },
  { key: 'jul', label: 'Jul', num: '07' },
  { key: 'aug', label: 'Aug', num: '08' },
  { key: 'sep', label: 'Sep', num: '09' },
  { key: 'oct', label: 'Oct', num: '10' },
  { key: 'nov', label: 'Nov', num: '11' },
  { key: 'dec', label: 'Dec', num: '12' }
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatServiceType = (serviceType) => {
  if (!serviceType) return '-';
  const upper = serviceType.toUpperCase();
  if (upper.includes('SWIMMING') || upper.includes('POOL')) {
    return 'Pool';
  }
  if (upper.includes('LANDSCAPE') || upper.includes('IRRIGATION')) {
    return 'Landscape';
  }
  return serviceType;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  } catch {
    return dateStr;
  }
};

// ============================================================================
// MONTH CELL COMPONENT
// ============================================================================

const MonthCell = ({ isActive, hasUpload }) => {
  if (!isActive) {
    return (
      <div className="month-cell inactive" />
    );
  }

  return (
    <div className={`month-cell ${hasUpload ? 'uploaded' : 'active'}`}>
      {hasUpload && (
        <CheckOutlined className="check-icon" />
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const WorkProgress = () => {
  // State
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [payees, setPayees] = useState([]);
  const [selectedPayee, setSelectedPayee] = useState('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [progressData, setProgressData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [statistics, setStatistics] = useState({
    totalPOs: 0,
    totalMonths: 0,
    uploadedCertificates: 0,
    completionRate: 0
  });

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  useEffect(() => {
    fetchYears();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchPayees();
      fetchProgressData();
    }
  }, [selectedYear]);

  useEffect(() => {
    if (selectedYear) {
      fetchProgressData();
    }
  }, [selectedPayee, serviceTypeFilter]);

  // Filter data based on search text
  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredData(progressData);
      return;
    }

    const lowerSearch = searchText.toLowerCase();
    const filtered = progressData.filter(item => 
      item.poNumber?.toLowerCase().includes(lowerSearch) ||
      item.cluster?.toLowerCase().includes(lowerSearch) ||
      item.payee?.toLowerCase().includes(lowerSearch)
    );
    setFilteredData(filtered);
  }, [searchText, progressData]);

  const fetchYears = async () => {
    try {
      const response = await loamsApi.get('/api/years/available');
      if (response.data.success) {
        setYears(response.data.years);
        if (response.data.years.length > 0) {
          setSelectedYear(response.data.current_year.toString());
        }
      }
    } catch (error) {
      console.error('Error fetching years:', error);
      setYears([2025, 2024, 2023]);
    }
  };

  const fetchPayees = async () => {
    try {
      const response = await loamsApi.get(`/api/file/unique-payees?year=${selectedYear}`);
      if (response.data.success) {
        setPayees(response.data.payees);
      }
    } catch (error) {
      console.error('Error fetching payees:', error);
    }
  };

  // ============================================================================
  // MAIN DATA FETCH
  // ============================================================================

  const fetchProgressData = async () => {
    setLoading(true);
    try {
      const year = parseInt(selectedYear);
      
      let url = `/api/work-progress/report?year=${year}`;
      if (selectedPayee) {
        url += `&payee=${encodeURIComponent(selectedPayee)}`;
      }
      if (serviceTypeFilter && serviceTypeFilter !== 'all') {
        url += `&service_type=${encodeURIComponent(serviceTypeFilter)}`;
      }

      const response = await loamsApi.get(url);
      
      if (response.data.success) {
        setProgressData(response.data.data);
        setFilteredData(response.data.data);
        setStatistics(response.data.statistics);
      } else {
        throw new Error(response.data.error || 'Failed to fetch progress data');
      }
      
    } catch (error) {
      console.error('Error fetching progress data:', error);
      message.error('Failed to load work progress data');
      setProgressData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // EXPORT TO EXCEL
  // ============================================================================

  const exportToExcel = async () => {
    try {
      setExporting(true);
      
      // Dynamic import of xlsx
      const XLSX = await import('xlsx');
      
      // Prepare data for Excel
      const excelData = filteredData.map((row, index) => {
        const rowData = {
          'S.No': index + 1,
          'PO Number': row.poNumber,
          'Cluster': row.cluster,
          'Start Date': formatDate(row.startDate),
          'End Date': formatDate(row.endDate),
          'Service Type': formatServiceType(row.serviceType),
          'Payee': row.payee || '-'
        };

        // Add month columns
        MONTHS.forEach(month => {
          const monthData = row.months[month.key];
          if (!monthData?.isActive) {
            rowData[month.label] = '';
          } else if (monthData?.hasUpload) {
            rowData[month.label] = '✓';
          } else {
            rowData[month.label] = '○';
          }
        });

        return rowData;
      });

      // Create workbook
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      worksheet['!cols'] = [
        { wch: 6 },   // S.No
        { wch: 18 },  // PO Number
        { wch: 25 },  // Cluster
        { wch: 12 },  // Start Date
        { wch: 12 },  // End Date
        { wch: 12 },  // Service Type
        { wch: 20 },  // Payee
        ...MONTHS.map(() => ({ wch: 5 })) // Month columns
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, `Progress ${selectedYear}`);

      // Add summary sheet
      const summaryData = [
        ['Work Progress Report Summary'],
        [''],
        ['Year', selectedYear],
        ['Generated On', new Date().toLocaleString()],
        [''],
        ['Statistics'],
        ['Total POs', statistics.totalPOs],
        ['Total Active Months', statistics.totalMonths],
        ['Certificates Uploaded', statistics.uploadedCertificates],
        ['Completion Rate', `${statistics.completionRate}%`],
        [''],
        ['Legend'],
        ['✓', 'Certificate Uploaded'],
        ['○', 'Active Month (Pending)'],
        ['', 'Not Applicable']
      ];

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

      // Generate and download
      const filename = `Work_Progress_${selectedYear}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, filename);

      message.success(`Exported to ${filename}`);

    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export. Please ensure SheetJS library is loaded.');
    } finally {
      setExporting(false);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedPayee('');
    setServiceTypeFilter('all');
    setSearchText('');
  };

  // ============================================================================
  // TABLE COLUMNS
  // ============================================================================

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 45,
      fixed: 'left',
      align: 'center',
      render: (_, __, index) => (
        <Text style={{ fontSize: '11px' }}>{index + 1}</Text>
      )
    },
    {
      title: 'PO Number',
      dataIndex: 'poNumber',
      key: 'poNumber',
      width: 140,
      fixed: 'left',
      ellipsis: true,
      render: (text) => (
        <Text strong style={{ fontSize: '11px' }}>{text}</Text>
      )
    },
    {
      title: 'Cluster',
      dataIndex: 'cluster',
      key: 'cluster',
      width: 160,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Text style={{ fontSize: '11px' }}>{text}</Text>
        </Tooltip>
      )
    },
    {
      title: 'Type',
      dataIndex: 'serviceType',
      key: 'serviceType',
      width: 70,
      align: 'center',
      render: (text) => (
        <Tag color={text?.toUpperCase().includes('POOL') ? 'blue' : 'green'} style={{ fontSize: '10px', margin: 0 }}>
          {formatServiceType(text)}
        </Tag>
      )
    },
    // Month columns
    ...MONTHS.map(month => ({
      title: month.label,
      key: month.key,
      width: 45,
      align: 'center',
      render: (_, record) => {
        const monthData = record.months?.[month.key] || {};
        return (
          <MonthCell 
            isActive={monthData.isActive}
            hasUpload={monthData.hasUpload}
          />
        );
      }
    }))
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="work-progress-container">
      {/* Filters Card */}
      <Card className="filters-card" bodyStyle={{ padding: '16px' }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={8} md={4}>
            <div className="filter-group">
              <Text className="filter-label">
                <CalendarOutlined /> Year
              </Text>
              <Select
                value={selectedYear}
                onChange={setSelectedYear}
                style={{ width: '100%' }}
                size="middle"
              >
                {years.map(year => (
                  <Option key={year} value={year.toString()}>{year}</Option>
                ))}
              </Select>
            </div>
          </Col>
          
          <Col xs={24} sm={8} md={5}>
            <div className="filter-group">
              <Text className="filter-label">
                <FilterOutlined /> Payee
              </Text>
              <Select
                value={selectedPayee}
                onChange={setSelectedPayee}
                placeholder="All Payees"
                style={{ width: '100%' }}
                size="middle"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {payees.map(p => (
                  <Option key={p.payee} value={p.payee}>
                    {p.payee}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={8} md={4}>
            <div className="filter-group">
              <Text className="filter-label">Service Type</Text>
              <Select
                value={serviceTypeFilter}
                onChange={setServiceTypeFilter}
                style={{ width: '100%' }}
                size="middle"
              >
                <Option value="all">All Types</Option>
                <Option value="swimming">Pool</Option>
                <Option value="landscape">Landscape</Option>
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <div className="filter-group">
              <Text className="filter-label">
                <SearchOutlined /> Search PO/Cluster
              </Text>
              <Input
                placeholder="Search PO or Cluster..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                prefix={<SearchOutlined style={{ color: '#999' }} />}
                allowClear
                size="middle"
              />
            </div>
          </Col>

          <Col xs={24} sm={12} md={5}>
            <div style={{ marginTop: 22 }}>
              <Space>
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={fetchProgressData}
                  loading={loading}
                  size="middle"
                >
                  Refresh
                </Button>
                <Button
                  icon={<ClearOutlined />}
                  onClick={clearFilters}
                  size="middle"
                >
                  Clear
                </Button>
                <Button
                  type="primary"
                  icon={<FileExcelOutlined />}
                  onClick={exportToExcel}
                  loading={exporting}
                  disabled={filteredData.length === 0}
                  style={{ backgroundColor: PHOENICIAN_GOLD, borderColor: PHOENICIAN_GOLD }}
                  size="middle"
                >
                  Export
                </Button>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Statistics Row */}
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} sm={6}>
          <Card size="small" className="stat-card">
            <Text type="secondary" className="stat-label">Total POs</Text>
            <div className="stat-value">{statistics.totalPOs}</div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" className="stat-card">
            <Text type="secondary" className="stat-label">Active Months</Text>
            <div className="stat-value">{statistics.totalMonths}</div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" className="stat-card">
            <Text type="secondary" className="stat-label">Uploaded</Text>
            <div className="stat-value" style={{ color: CERTIFICATE_CHECK_COLOR }}>
              {statistics.uploadedCertificates}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" className="stat-card">
            <Text type="secondary" className="stat-label">Completion</Text>
            <div className="stat-value" style={{ color: PHOENICIAN_GOLD }}>
              {statistics.completionRate}%
            </div>
            <Progress 
              percent={statistics.completionRate} 
              showInfo={false}
              strokeColor={PHOENICIAN_GOLD}
              size="small"
              className="stat-progress"
            />
          </Card>
        </Col>
      </Row>

      {/* Legend */}
      <Card size="small" className="legend-card">
        <Space size="large" wrap>
          <Space size="small">
            <div className="legend-box active" />
            <Text className="legend-text">Active (Pending)</Text>
          </Space>
          <Space size="small">
            <div className="legend-box uploaded">
              <CheckOutlined className="legend-check" />
            </div>
            <Text className="legend-text">Certificate Uploaded</Text>
          </Space>
          <Space size="small">
            <div className="legend-box inactive" />
            <Text className="legend-text">Not Applicable</Text>
          </Space>
        </Space>
      </Card>

      {/* Main Table */}
      <Card 
        className="table-card"
        bodyStyle={{ padding: 0 }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Spin size="large" />
            <Text style={{ display: 'block', marginTop: 16, color: '#666' }}>Loading progress data...</Text>
          </div>
        ) : filteredData.length === 0 ? (
          <Empty 
            description="No data found"
            style={{ padding: '60px 20px' }}
          />
        ) : (
          <Table
            dataSource={filteredData}
            columns={columns}
            rowKey="key"
            className="work-progress-table"
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              pageSizeOptions: ['25', '50', '100', '200'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} POs`,
              size: 'small'
            }}
            scroll={{ x: 1200, y: 'calc(100vh - 480px)' }}
            size="small"
            bordered
          />
        )}
      </Card>
    </div>
  );
};

export default WorkProgress;